import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Slider,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from '@mui/material';
import * as d3 from 'd3';
import { ToastContainer, toast } from 'react-toastify';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';

import {
  DataGrid,
  GridColDef,
  GridToolbarColumnsButton,
  GridToolbarContainer,
  GridToolbarDensitySelector,
  GridToolbarExport,
} from '@mui/x-data-grid';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { COLOR_GREY_400, COLOR_PRIMARY_1, COLOR_PRIMARY_2, SIDEBAR_COLOR } from 'src/colors';
import { BOOKING_STATUS_COLOR, COLOR_PRIMARY, countryListWithCode, exchangeRates } from 'src/const';
import {
  aggregateToMarketLevel,
  convertAdvancedMarkersToMarkers,
  convertCurrency,
  extractVisibleListings,
  formatCurrency,
  generateDateList,
  getFilteredListings,
  getRateStatus,
  calculatePriceTiers,
  calculateRevenuePotentialRange,
  getPreviousTimePeriod,
  dateDeforeNDays,
  getDaysInMonth,
  isYearMonthKey,
} from 'src/utils/functions';
import DemoBookingForm from 'src/App/Components/Forms/DemoBookingForm';
import {
  getExplorerMarkets,
  getAdvancedMarkers,
  getFutureDailyData,
  updatePriceTiers,
} from 'src/store/Dashboard/actions';
import { Filter } from 'src/App/HOC';
import { makeStyles } from '@material-ui/core/styles';
import { FilterVal, ListingsProp } from 'src/interfaces/iproptypes';
import ListingModal from './ListingModal';

const CustomToolbar = ({fileName}) => {
  return (
    <GridToolbarContainer>
      <GridToolbarColumnsButton />
      <GridToolbarDensitySelector />
      <GridToolbarExport csvOptions={{ fileName: fileName }} />
    </GridToolbarContainer>
  );
};

const getKPIPercentageChange = (timePeriod, prevTimePeriod, data, dataKey) => {
  if (!timePeriod || !prevTimePeriod || !data[timePeriod] || !data[prevTimePeriod]) return null;
  const change = parseFloat(
    (
      ((data[timePeriod][dataKey] - data[prevTimePeriod][dataKey]) / data[timePeriod][dataKey]) *
      100
    ).toFixed(2)
  );
  return change;
};
const useStyles = makeStyles({
  greyRow: {
    backgroundColor: `${COLOR_GREY_400} !important`,
    '&:hover': {
      backgroundColor: `${COLOR_GREY_400} !important`,
    },
  },
});

const getMarketExplorerData = (markets, listingRange, timePeriod) => {
  let rows: any = [];
  const lowerRangeValue = listingRange[0];
  const upperRangeValue = listingRange[1];
  const prevTimePeriod = getPreviousTimePeriod(timePeriod);
  const prevToPrevTimePeriod = getPreviousTimePeriod(prevTimePeriod);
  let i = 0;
  for (let data of markets) {
    if (
      data[timePeriod]?.total_supply < lowerRangeValue ||
      data[timePeriod]?.total_supply > upperRangeValue
    ) {
      continue;
    }
    rows.push({
      id: i,
      marketName: data['district'],
      marketState: data['city'],
      annualSupply: data[timePeriod]?.total_supply ?? null,
      supplyPercChange: getKPIPercentageChange(timePeriod, prevTimePeriod, data, 'total_supply'),
      supplyPercChangeTwoYears: getKPIPercentageChange(
        timePeriod,
        prevToPrevTimePeriod,
        data,
        'total_supply'
      ),
      hostStat1To5: data[timePeriod]?.['1-5_host'] ?? null,
      hostStat6To20: data[timePeriod]?.['6-20_host'] ?? null,
      hostStat20Plus: data[timePeriod]?.['20+_host'] ?? null,
      percStillAvailiableFromLastYear: data[timePeriod]?.yoy_supply_share ?? null,
      AvgOccupancyRate: data[timePeriod]?.average_occupancy_rate ?? null,
      AvgOccupancyRatePercChange: getKPIPercentageChange(
        timePeriod,
        prevTimePeriod,
        data,
        'average_occupancy_rate'
      ),
      AvgOccupancyRatePercChangeTwoYears: getKPIPercentageChange(
        timePeriod,
        prevToPrevTimePeriod,
        data,
        'average_occupancy_rate'
      ),
      adr: data[timePeriod]?.adr ?? null,
      adrPercChange: getKPIPercentageChange(timePeriod, prevTimePeriod, data, 'adr'),
      adrPercChangeTwoYears: getKPIPercentageChange(timePeriod, prevToPrevTimePeriod, data, 'adr'),
      revenue: data[timePeriod]?.revenue ?? null,
      revenuePercChange: getKPIPercentageChange(timePeriod, prevTimePeriod, data, 'revenue'),
      revenuePercChangeTwoYears: getKPIPercentageChange(
        timePeriod,
        prevToPrevTimePeriod,
        data,
        'revenue'
      ),
      avgLOS: null,
      avgLOSPercChange: null,
      avgBookingLeadTime: null,
      avgBookingLeadTimePercChange: null,
    });
    i += 1;
  }

  return rows;
};

const aggregateMarketLevelPropertyManagerData = (
  Listings,
  listingRange,
  timePeriod,
  globalFilteredListingIds,
  filterActive
) => {
  let rows: any = [];
  let i = 0;
  let lowerRangeValue = listingRange[0];
  let upperRangeValue = listingRange[1];
  let hostData = {};

  if (Listings) {
    Listings.forEach(data => {
      if (
        !filterActive ||
        (filterActive && globalFilteredListingIds.includes(String(data['listingID'])))
      ) {
        if (!hostData.hasOwnProperty(data['host_id'])) {
          hostData[data['host_id']] = {
            listingCount: 1,
            all_occupancy: [data[`avg_occupancy_rate_${timePeriod}`] || 0],
            all_adr: [data[`adr_${timePeriod}`] || 0],
            all_revenue: [data[`annual_revenue_${timePeriod}`] || 0],
            all_reviews: data['visible_review_count'] || 0,
            all_rating: data['reveiw_scores_rating'] ? [data['reveiw_scores_rating']] : [],
            name: data['host_name'],
          };
        } else {
          hostData[data['host_id']]['listingCount'] += 1;
          hostData[data['host_id']]['all_occupancy'].push(
            data[`avg_occupancy_rate_${timePeriod}`] || 0
          );
          hostData[data['host_id']]['all_adr'].push(data[`adr_${timePeriod}`] || 0);
          hostData[data['host_id']]['all_revenue'].push(data[`annual_revenue_${timePeriod}`] || 0);
          hostData[data['host_id']]['all_reviews'] += data['visible_review_count'] || 0;
          if (data['reveiw_scores_rating'])
            hostData[data['host_id']]['all_rating'].push(data['reveiw_scores_rating']);
        }
      }
    });

    Object.keys(hostData).forEach(host_id => {
      const data = hostData[host_id];
      data['mean_adr'] = parseFloat(
        (data['all_adr'].reduce((a, b) => a + b, 0) / data['all_adr'].length).toFixed(1)
      );
      data['mean_revenue'] = parseFloat(
        (data['all_revenue'].reduce((a, b) => a + b, 0) / data['all_revenue'].length).toFixed(1)
      );
      data['mean_rating'] =
        data['all_rating'].length > 0
          ? parseFloat(
              (data['all_rating'].reduce((a, b) => a + b, 0) / data['all_rating'].length).toFixed(1)
            )
          : null;
    });

    for (let host_id in hostData) {
      const data = hostData[host_id];
      if (data['listingCount'] >= lowerRangeValue && data['listingCount'] <= upperRangeValue) {
        rows.push({
          id: i,
          hostID: host_id,
          hostName: data['name'],
          noOfListings: data['listingCount'],
          adr: data['mean_adr'],
          revenue: data['mean_revenue'],
          propertyReviews: data['all_reviews'],
          avgPropertyRating: data['mean_rating'],
        });
        i += 1;
      }
    }
  }

  return rows;
};

const getMarketSummaryData = (
  historicalData,
  startMonth,
  startYear,
  endMonth,
  endYear,
  granualityType,
  currency
) => {
  let months = [
    '2021-01',
    '2021-02',
    '2021-03',
    '2021-04',
    '2021-05',
    '2021-06',
    '2021-07',
    '2021-08',
    '2021-09',
    '2021-10',
    '2021-11',
    '2021-12',
    '2022-01',
    '2022-02',
    '2022-03',
    '2022-04',
    '2022-05',
    '2022-06',
    '2022-07',
    '2022-08',
    '2022-09',
    '2022-10',
    '2022-11',
    '2022-12',
    '2023-01',
    '2023-02',
    '2023-03',
    '2023-04',
    '2023-05',
    '2023-06',
    '2023-07',
    '2023-08',
    '2023-09',
    '2023-10',
    '2023-11',
    '2023-12',
    '2024-01',
    '2024-02',
    '2024-03',
    '2024-04',
  ];
  let years = [2021, 2022, 2023, 2024];
  let quarterlyMonths = {
    'Q1 2021': ['2021-01', '2021-02', '2021-03'],
    'Q2 2021': ['2021-04', '2021-05', '2021-06'],
    'Q3 2021': ['2021-07', '2021-08', '2021-09'],
    'Q4 2021': ['2021-10', '2021-11', '2021-12'],
    'Q1 2022': ['2022-01', '2022-02', '2022-03'],
    'Q2 2022': ['2022-04', '2022-05', '2022-06'],
    'Q3 2022': ['2022-07', '2022-08', '2022-09'],
    'Q4 2022': ['2022-10', '2022-11', '2022-12'],
    'Q1 2023': ['2023-01', '2023-02', '2023-03'],
    'Q2 2023': ['2023-04', '2023-05', '2023-06'],
    'Q3 2023': ['2023-07', '2023-08', '2023-09'],
    'Q4 2023': ['2023-10', '2023-11', '2023-12'],
    'Q1 2024': ['2024-01', '2024-02', '2024-03'],
    'Q2 2024 (till April)': ['2024-04'],
  };
  let rows: any = [];
  let i = 0;
  let formattedStartMonth = startMonth;
  let formattedEndMonth = endMonth;
  if (startMonth.toString().length == 1) {
    formattedStartMonth = '0' + startMonth;
  }
  if (endMonth.toString().length == 1) {
    formattedEndMonth = '0' + endMonth;
  }
  let startDate = `${startYear}-${formattedStartMonth}`;
  let endDate = `${endYear}-${formattedEndMonth}`;

  if (granualityType == 'monthly') {
    for (let month of months) {
      if (month >= startDate && month <= endDate) {
        let data = historicalData[month] ? historicalData[month] : null;
        rows.push({
          id: i,
          date: month,
          availiableListings: data.availiableListingsCount,
          bookedListings: data.bookedListingsCount,
          availiableListingNights: data.availiableListingNights,
          bookedNights: data.bookedNightsCount,
          revenue: data.revenue,
          avgOccupancy: data.occupancyRates,
          adr: data.adrRates,
          avgLOS: null,
          avgBookingLeadTime: null,
        });
        i += 1;
      }
    }
  }
  if (granualityType == 'quaterly') {
    for (let quarter of Object.keys(quarterlyMonths)) {
      let totalRevenue = 0;
      let totalAvailiableListings = 0;
      let totalAvailiableListingNights = 0;
      let totalBookedListings = 0;
      let totalBookedNights = 0;
      let allOccupancy: any = [];
      let allAdr: any = [];
      for (let month of quarterlyMonths[quarter]) {
        let data = historicalData[month];
        totalRevenue += data.revenue;
        totalAvailiableListings += data.availiableListingsCount;
        totalAvailiableListingNights += data.availiableListingNights;
        totalBookedListings += data.bookedListingsCount;
        totalBookedNights += data.bookedNightsCount;
        allOccupancy.push(data.occupancyRates);
        allAdr.push(data.adrRates);
      }
      rows.push({
        id: i,
        date: quarter,
        availiableListings: totalAvailiableListings,
        bookedListings: totalBookedListings,
        availiableListingNights: totalAvailiableListingNights,
        bookedNights: totalBookedNights,
        revenue: totalRevenue,
        avgOccupancy: allOccupancy.length > 0 ? Math.round(d3.mean(allOccupancy)) : -1,
        adr: allAdr.length > 0 ? Math.round(d3.mean(allAdr)) : -1,
        avgLOS: null,
        avgBookingLeadTime: null,
      });
      i += 1;
    }
  }
  if (granualityType == 'annual') {
    for (let year of years) {
      let totalRevenue = 0;
      let totalAvailiableListings = 0;
      let totalAvailiableListingNights = 0;
      let totalBookedListings = 0;
      let totalBookedNights = 0;
      let allOccupancy: any = [];
      let allAdr: any = [];
      for (let month of months) {
        if (month.includes(year.toString())) {
          let data = historicalData[month];
          totalRevenue += data.revenue;
          totalAvailiableListings += data.availiableListingsCount;
          totalAvailiableListingNights += data.availiableListingNights;
          totalBookedListings += data.bookedListingsCount;
          totalBookedNights += data.bookedNightsCount;
          allOccupancy.push(data.occupancyRates);
          allAdr.push(data.adrRates);
        }
      }
      rows.push({
        id: i,
        date: year == 2024 ? '2024 (till April)' : year,
        availiableListings: totalAvailiableListings,
        bookedListings: totalBookedListings,
        availiableListingNights: totalAvailiableListingNights,
        bookedNights: totalBookedNights,
        revenue: totalRevenue,
        avgOccupancy: allOccupancy.length > 0 ? Math.round(d3.mean(allOccupancy)) : -1,
        adr: allAdr.length > 0 ? Math.round(d3.mean(allAdr)) : -1,
        avgLOS: null,
        avgBookingLeadTime: null,
      });
      i += 1;
    }
  }

  return rows;
};

const getCompsData = (
  allData,
  futureData,
  globalFilteredListingIds,
  filterActive,
  listingId,
  timePeriod,
  currency,
  granualityType,
  dateList,
  months
) => {
  let rows: any = [];
  let i = 0;
  let activeDaysMapping = {
    ltm: 'active_days_ltm',
    '2023': 'active_days_2023',
    '2022': 'active_days_2022',
    '2021': 'active_days_2021',
  };

  let revenueMapping = {
    ltm: 'annual_revenue_ltm',
    '2023': 'annual_revenue_2023',
    '2022': 'annual_revenue_2022',
    '2021': 'annual_revenue_2021',
  };

  let occupancyMapping = {
    ltm: 'avg_occupancy_rate_ltm',
    '2023': 'avg_occupancy_rate_2023',
    '2022': 'avg_occupancy_rate_2022',
    '2021': 'avg_occupancy_rate_2021',
  };

  let dailyRateMapping = {
    ltm: 'adr_ltm',
    '2023': 'adr_2023',
    '2022': 'adr_2022',
    '2021': 'adr_2021',
  };

  for (let data of allData) {
    if (filterActive && !globalFilteredListingIds.includes(String(data['listingID']))) continue;
    let listingFutureData = {} as any;
    Object.values(futureData).forEach((value: any) => {
      if (value['listingID'] == data['listingID']) {
        listingFutureData = value;
      }
    });
    // console.log(">>>>",listingFutureData)
    let listingLevelData = {};
    if (data['listingID'].toString().includes(listingId)) {
      listingLevelData = {
        id: i,
        title: data['name'],
        propertyType: data['room_and_property_type'],
        listingType: data['room_type'],
        lastScrappedDate: data['crawled_on'],
        country: data['country_code'],
        city: data['city'],
        noOfReviews: data['visible_review_count'],
        bedrooms: data['bedrooms'],
        bathrooms: data['bathrooms'],
        maxGuests: data['accommodates'],
        airbnbSuperhost: data['is_superhost'],
        cancellationPolicy: data['cancellation_policy'],
        cleaningFee: data['cleaning_fee'],
        extraPeopleFee: data['extra_fee_person_estimate'],
        checkin: data['checkin'],
        checkout: data['checkout'],
        minimumStay: data['minimum_nights'],
        latitude: data['latitude'],
        longitude: data['longitude'],
        exactLocation: data['is_location_exact'],
        overallRating: data['reveiw_scores_rating'],
        communicationRating: data['communication_rating'],
        accuracyRating: data['accuracy_rating'],
        cleanlinessRating: data['cleanliness_rating'],
        checkinRating: data['checkin_rating'],
        locationRating: data['location_rating'],
        valueRating: data['value_rating'],
        amenities: JSON.stringify(data['amenities']),
        pictureURL: data['thumbnail_url'],
        licence: data['license'],
        listingID: data['listingID'],
        hostID: data['host_id'],
        guestControls: JSON.stringify(data['guest_controls']),
        instantBookable: data['instant_bookable'],
        petsAllowed: `${data['guest_controls']['allows_pets']}`,
        listingURL: `http://airbnb.com/rooms/${data['listingID']}`,
        lastSeen: data['last_seen'],
        [`availiableDays_${timePeriod}`]: Math.min(data[activeDaysMapping[timePeriod]], 365),
        [`blockedDays_${timePeriod}`]: Math.max(365 - data[activeDaysMapping[timePeriod]], 0),
        [`occupancyRate_${timePeriod}`]: data[occupancyMapping[timePeriod]],
        [`adr_${timePeriod}`]: data[dailyRateMapping[timePeriod]],
        [`revenue_${timePeriod}`]: data[revenueMapping[timePeriod]],
        [`revenuePotential_${timePeriod}`]: data[revenueMapping[timePeriod]]
          ? Math.round(
              (data[revenueMapping[timePeriod]] / data[activeDaysMapping[timePeriod]]) * 366
            )
          : null,
      };

      {
        granualityType == 'monthly' &&
          months.forEach(month => {
            const daysInMonth = getDaysInMonth(month);
            if (data[month]) {
              let destructData = JSON.parse(data[month]);
              const [adr, activeDays, bookedDays, noOfBookings] = destructData;
              let occupancy: number | null = null;
              let revenue: number | null = null;
              if (activeDays > 0) {
                occupancy = Math.round(Math.min((bookedDays / activeDays) * 100 * 1.2, 100));
                revenue = Math.round(
                  adr * (occupancy / 100) * activeDays +
                    (data['cleaning_fee'] ? noOfBookings * data['cleaning_fee'] : 0)
                );
              }

              listingLevelData[`availiableDays_${month}`] = Math.min(activeDays, daysInMonth);
              listingLevelData[`blockedDays_${month}`] = Math.max(daysInMonth - activeDays, 0);
              listingLevelData[`occupancyRate_${month}`] = occupancy;
              listingLevelData[`adr_${month}`] = adr;
              listingLevelData[`revenue_${month}`] = revenue;
            } else {
              listingLevelData[`availiableDays_${month}`] = 0;
              listingLevelData[`blockedDays_${month}`] = daysInMonth;
              listingLevelData[`occupancyRate_${month}`] = null;
              listingLevelData[`adr_${month}`] = null;
              listingLevelData[`revenue_${month}`] = null;
            }
          });
      }

      {
        granualityType == 'daily' &&
          dateList.map(date => {
            let futureRate = listingFutureData[date];
            const status: 'booked' | 'recentlyBooked' | 'available' | 'inactive' = getRateStatus(
              futureRate
            );
            futureRate = futureRate?.replace(/:b|:rb|:a|:na/g, '');
            futureRate = futureRate?.replace('$', '');
            if (futureRate?.includes('->')) {
              const ratesArr = futureRate?.split(' -> ');
              futureRate = ratesArr[1];
            }
            listingLevelData[`${date}`] = `${futureRate}+${status}`;
            if (futureRate == undefined) {
              listingLevelData[`${date}`] = ` +inactive`;
            }
          });
      }

      rows.push(listingLevelData);
      i += 1; // Increment i within the condition block
    }
  }

  return rows;
};

const CustomFilters = ({
  toggleDrawer,
  marketType,
  setMarketType,
  timePeriod,
  setTimePeriod,
  mapSelection,
  setMapSelection,
  listingRange,
  setListingRange,
  filterUsedIn,
  openDemoBookingForm,
}) => {
  const listingRangeMarks = [
    {
      value: filterUsedIn == 'marketExplorer' ? 10 : 1,
      label: filterUsedIn == 'marketExplorer' ? '100' : '1',
    },
    {
      value: filterUsedIn == 'marketExplorer' ? 100000 : 155,
      label: filterUsedIn == 'marketExplorer' ? '100K+ listings' : '155 listings',
    },
  ];

  const ListingRangeChange = (event: Event, newValue: number | number[]) => {
    setListingRange(newValue as number[]);
  };
  return (
    <Grid container item xs={12}>
      {filterUsedIn == 'marketExplorer' && (
        <Grid item xs={2}>
          <Typography sx={{ fontSize: '12px' }}>Choose Comaprison Level</Typography>
          <FormControl sx={{ margin: '5px 5px 0px 0px', width: '80%' }}>
            <Select
              labelId="demo-select-small-label"
              id="demo-select-small"
              value={marketType}
              label=""
              onChange={e => {
                setMarketType(e.target.value);
              }}
              sx={{
                '& .MuiSelect-select.MuiSelect-outlined.MuiOutlinedInput-input.MuiInputBase-input.css-11u53oe-MuiSelect-select-MuiInputBase-input-MuiOutlinedInput-input': {
                  padding: '5px',
                },
              }}
            >
              <MenuItem value={'market'}>Market</MenuItem>
              <MenuItem value={'submarket'}>Submarket</MenuItem>
            </Select>
          </FormControl>
        </Grid>
      )}
      <Grid item xs={4}>
        <Typography sx={{ fontSize: '12px' }}>
          {filterUsedIn == 'marketExplorer'
            ? 'Market/Submarket/Zipcode Filter by Annual Supply'
            : 'Host Listing Count Filter'}
        </Typography>
        <Box sx={{ width: '70%', padding: '5px' }}>
          <Slider
            size="small"
            value={listingRange}
            min={listingRangeMarks[0]['value']}
            max={listingRangeMarks[1]['value']}
            step={filterUsedIn == 'marketExplorer' ? 100 : 10}
            marks={listingRangeMarks}
            onChange={ListingRangeChange}
            valueLabelDisplay="auto"
            sx={{
              color: COLOR_PRIMARY,
            }}
          />
        </Box>
      </Grid>
      <Grid item xs={2}>
        <Typography sx={{ fontSize: '12px' }}>Choose Periods:</Typography>
        <FormControl sx={{ margin: '5px 5px 0px 0px', width: '80%' }}>
          <Select
            labelId="demo-select-small-label"
            id="demo-select-small"
            value={timePeriod}
            label=""
            onChange={e => {
              setTimePeriod(e.target.value);
            }}
            sx={{
              '& .MuiSelect-select.MuiSelect-outlined.MuiOutlinedInput-input.MuiInputBase-input.css-11u53oe-MuiSelect-select-MuiInputBase-input-MuiOutlinedInput-input': {
                padding: '5px',
              },
            }}
          >
            <MenuItem value={'ltm'}>LTM</MenuItem>
            <MenuItem value={'2023'}>2023</MenuItem>
            <MenuItem value={'2022'}>2022</MenuItem>
            <MenuItem value={'2021'}>2021</MenuItem>
          </Select>
        </FormControl>
      </Grid>
      {/* <Grid item xs={2}>
        <Typography sx={{ fontSize: '12px' }}>Map Selection</Typography>
        <FormControl sx={{ margin: '5px 5px 0px 0px', width: '80%' }}>
          <Select
            labelId="demo-select-small-label"
            id="demo-select-small"
            value={mapSelection}
            label=""
            onChange={e => {
              setMapSelection(e.target.value);
            }}
            sx={{
              '& .MuiSelect-select.MuiSelect-outlined.MuiOutlinedInput-input.MuiInputBase-input.css-11u53oe-MuiSelect-select-MuiInputBase-input-MuiOutlinedInput-input': {
                padding: '5px',
              },
            }}
          >
            <MenuItem value={'all'}>All Properties</MenuItem>
            <MenuItem value={'map'}>Map Selection</MenuItem>
          </Select>
        </FormControl>
      </Grid> */}
      {filterUsedIn != 'marketExplorer' && (
        <Grid item xs={2} sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <Button
            className="mapFilterButton"
            size="small"
            onClick={() => toggleDrawer()}
            sx={{
              fontSize: '16',
              background: COLOR_PRIMARY_2,
              color: '#ffffff',
              '&:hover': { backgroundColor: COLOR_PRIMARY_1 },
            }}
          >
            <FilterAltOutlinedIcon sx={{ fontSize: 18, color: 'inherit' }} />
            All Filters
          </Button>
        </Grid>
      )}
    </Grid>
  );
};

const CustomCompsFilters = ({
  listingId,
  setListingID,
  toggleDrawer,
  timePeriod,
  setTimePeriod,
  mapSelection,
  setMapSelection,
  openDemoBookingForm,
  granualityType,
  setGranualityType,
  compsRowsData,
}) => {
  const multipleAdvancedListingsCallLoading = useSelector(
    state => (state as any).dashboard.multipleAdvancedListingsCallLoading
  );

  return (
    <Grid container item xs={12}>
      <Grid item xs={2}>
        <Typography sx={{ fontSize: '12px' }}>Data Granularity</Typography>
        <FormControl sx={{ margin: '10px 5px 0px 0px', width: '80%' }}>
          <Select
            labelId="demo-select-small-label"
            id="demo-select-small"
            value={granualityType}
            label=""
            onChange={e => {
              setGranualityType(e.target.value);
            }}
            sx={{
              '& .MuiSelect-select.MuiSelect-outlined.MuiOutlinedInput-input.MuiInputBase-input.css-11u53oe-MuiSelect-select-MuiInputBase-input-MuiOutlinedInput-input': {
                padding: '5px',
              },
            }}
          >
            <MenuItem value={'daily'}>Daily</MenuItem>
            <MenuItem value={'monthly'}>Monthly</MenuItem>
            <MenuItem value={'yearly'}>Yearly</MenuItem>
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={2}>
        <Typography sx={{ fontSize: '12px' }}>Listing ID:</Typography>
        <FormControl sx={{ margin: '5px 5px 0px 0px', width: '80%' }}>
          <Select
            labelId="demo-simple-select-label"
            id="demo-simple-select"
            value={listingId}
            label=""
            onChange={e => {
              setListingID(e.target.value);
            }}
            sx={{
              '& .MuiSelect-select.MuiSelect-outlined.MuiOutlinedInput-input.MuiInputBase-input.css-11u53oe-MuiSelect-select-MuiInputBase-input-MuiOutlinedInput-input': {
                padding: '5px',
              },
            }}
          >
            {compsRowsData &&
              compsRowsData.map(data => (
                <MenuItem key={data.listingID} value={data.listingID}>
                  {data.listingID}
                </MenuItem>
              ))}
            <MenuItem key={''} value={''}>
              Show All
            </MenuItem>
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={2}>
        <Typography sx={{ fontSize: '12px' }}>Choose Periods:</Typography>
        <FormControl
          sx={{ margin: '5px 5px 0px 0px', width: '80%' }}
          disabled={granualityType !== 'yearly'}
        >
          <Select
            labelId="demo-select-small-label"
            id="demo-select-small"
            value={timePeriod}
            label=""
            onChange={e => {
              setTimePeriod(e.target.value);
            }}
            sx={{
              '& .MuiSelect-select.MuiSelect-outlined.MuiOutlinedInput-input.MuiInputBase-input.css-11u53oe-MuiSelect-select-MuiInputBase-input-MuiOutlinedInput-input': {
                padding: '5px',
              },
            }}
          >
            <MenuItem value={'ltm'}>LTM</MenuItem>
            <MenuItem value={'2023'}>2023</MenuItem>
            <MenuItem value={'2022'}>2022</MenuItem>
            <MenuItem value={'2021'}>2021</MenuItem>
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={2}>
        <Typography sx={{ fontSize: '12px' }}>Map Selection</Typography>
        <FormControl sx={{ margin: '5px 5px 0px 0px', width: '80%' }}>
          <Select
            labelId="demo-select-small-label"
            id="demo-select-small"
            value={mapSelection}
            label=""
            onChange={e => {
              setMapSelection(e.target.value);
            }}
            sx={{
              '& .MuiSelect-select.MuiSelect-outlined.MuiOutlinedInput-input.MuiInputBase-input.css-11u53oe-MuiSelect-select-MuiInputBase-input-MuiOutlinedInput-input': {
                padding: '5px',
              },
            }}
          >
            <MenuItem value={'all'}>All Properties</MenuItem>
            <MenuItem value={'map'}>Map Selection</MenuItem>
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={2} sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <Button
          className="mapFilterButton"
          size="small"
          onClick={() => toggleDrawer()}
          sx={{
            fontSize: '16',
            background: COLOR_PRIMARY_2,
            color: '#ffffff',
            '&:hover': { backgroundColor: COLOR_PRIMARY_1 },
          }}
        >
          <FilterAltOutlinedIcon sx={{ fontSize: 18, color: 'inherit' }} />
          All Filters
        </Button>
      </Grid>

      {multipleAdvancedListingsCallLoading && (
        <Grid item xs={2} sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <CircularProgress />
        </Grid>
      )}
    </Grid>
  );
};

const CustomMarketSummaryFilters = ({
  toggleDrawer,
  granualityType,
  setGranualityType,
  mapSelection,
  setMapSelection,
  startMonth,
  setStartMonth,
  startYear,
  setStartYear,
  endMonth,
  setEndMonth,
  endYear,
  setEndYear,
  openDemoBookingForm,
}) => {
  return (
    <Grid container item xs={12}>
      <Grid item xs={2}>
        <Typography sx={{ fontSize: '12px' }}>Data Granularity</Typography>
        <FormControl sx={{ margin: '10px 5px 0px 0px', width: '80%' }}>
          <Select
            labelId="demo-select-small-label"
            id="demo-select-small"
            value={granualityType}
            label=""
            onChange={e => {
              setGranualityType(e.target.value);
            }}
            sx={{
              '& .MuiSelect-select.MuiSelect-outlined.MuiOutlinedInput-input.MuiInputBase-input.css-11u53oe-MuiSelect-select-MuiInputBase-input-MuiOutlinedInput-input': {
                padding: '5px',
              },
            }}
          >
            <MenuItem value={'monthly'}>Monthly</MenuItem>
            <MenuItem value={'quaterly'}>Quarterly</MenuItem>
            <MenuItem value={'annual'}>Annual</MenuItem>
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={3}>
        <Typography sx={{ fontSize: '12px' }}>Select Start Date</Typography>
        <FormControl sx={{ margin: '10px 5px 0px 0px', width: '40%' }}>
          <InputLabel id="demo-select-small-label">Month</InputLabel>
          <Select
            labelId="demo-select-small-label"
            id="demo-select-small"
            value={startMonth}
            label="Month"
            onChange={e => {
              setStartMonth(e.target.value);
            }}
            disabled={granualityType !== 'monthly'}
            sx={{
              '& .MuiSelect-select.MuiSelect-outlined.MuiOutlinedInput-input.MuiInputBase-input.css-11u53oe-MuiSelect-select-MuiInputBase-input-MuiOutlinedInput-input': {
                padding: '5px',
              },
            }}
          >
            <MenuItem value={1}>Jan</MenuItem>
            <MenuItem value={2}>Feb</MenuItem>
            <MenuItem value={3}>Mar</MenuItem>
            <MenuItem value={4}>Apr</MenuItem>
            <MenuItem value={5}>May</MenuItem>
            <MenuItem value={6}>Jun</MenuItem>
            <MenuItem value={7}>Jul</MenuItem>
            <MenuItem value={8}>Aug</MenuItem>
            <MenuItem value={9}>Sep</MenuItem>
            <MenuItem value={10}>Oct</MenuItem>
            <MenuItem value={11}>Nov</MenuItem>
            <MenuItem value={12}>Dec</MenuItem>
          </Select>
        </FormControl>
        <FormControl sx={{ margin: '10px 5px 0px 0px', width: '40%' }}>
          <InputLabel id="demo-select-small-label">Year</InputLabel>
          <Select
            labelId="demo-select-small-label"
            id="demo-select-small"
            value={startYear}
            label="Year"
            onChange={e => {
              setStartYear(e.target.value);
            }}
            disabled={granualityType !== 'monthly'}
            sx={{
              '& .MuiSelect-select.MuiSelect-outlined.MuiOutlinedInput-input.MuiInputBase-input.css-11u53oe-MuiSelect-select-MuiInputBase-input-MuiOutlinedInput-input': {
                padding: '5px',
              },
            }}
          >
            <MenuItem value={2021}>2021</MenuItem>
            <MenuItem value={2022}>2022</MenuItem>
            <MenuItem value={2023}>2023</MenuItem>
            <MenuItem value={2024}>2024</MenuItem>
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={3}>
        <Typography sx={{ fontSize: '12px' }}>Select End Date</Typography>
        <FormControl sx={{ margin: '10px 5px 0px 0px', width: '40%' }}>
          <InputLabel id="demo-select-small-label">Month</InputLabel>
          <Select
            labelId="demo-select-small-label"
            id="demo-select-small"
            value={endMonth}
            label="Month"
            onChange={e => {
              setEndMonth(e.target.value);
            }}
            disabled={granualityType !== 'monthly'}
            sx={{
              '& .MuiSelect-select.MuiSelect-outlined.MuiOutlinedInput-input.MuiInputBase-input.css-11u53oe-MuiSelect-select-MuiInputBase-input-MuiOutlinedInput-input': {
                padding: '5px',
              },
            }}
          >
            <MenuItem value={1}>Jan</MenuItem>
            <MenuItem value={2}>Feb</MenuItem>
            <MenuItem value={3}>Mar</MenuItem>
            <MenuItem value={4}>Apr</MenuItem>
            <MenuItem value={5}>May</MenuItem>
            <MenuItem value={6}>Jun</MenuItem>
            <MenuItem value={7}>Jul</MenuItem>
            <MenuItem value={8}>Aug</MenuItem>
            <MenuItem value={9}>Sep</MenuItem>
            <MenuItem value={10}>Oct</MenuItem>
            <MenuItem value={11}>Nov</MenuItem>
            <MenuItem value={12}>Dec</MenuItem>
          </Select>
        </FormControl>
        <FormControl sx={{ margin: '10px 5px 0px 0px', width: '40%' }}>
          <InputLabel id="demo-select-small-label">Year</InputLabel>
          <Select
            labelId="demo-select-small-label"
            id="demo-select-small"
            value={endYear}
            label="Year"
            onChange={e => {
              setEndYear(e.target.value);
            }}
            disabled={granualityType !== 'monthly'}
            sx={{
              '& .MuiSelect-select.MuiSelect-outlined.MuiOutlinedInput-input.MuiInputBase-input.css-11u53oe-MuiSelect-select-MuiInputBase-input-MuiOutlinedInput-input': {
                padding: '5px',
              },
            }}
          >
            <MenuItem value={2021}>2021</MenuItem>
            <MenuItem value={2022}>2022</MenuItem>
            <MenuItem value={2023}>2023</MenuItem>
            <MenuItem value={2024}>2024</MenuItem>
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={2}>
        <Typography sx={{ fontSize: '12px' }}>Map Selection</Typography>
        <FormControl sx={{ margin: '10px 5px 0px 0px', width: '80%' }}>
          <Select
            labelId="demo-select-small-label"
            id="demo-select-small"
            value={mapSelection}
            label=""
            onChange={e => {
              setMapSelection(e.target.value);
            }}
            sx={{
              '& .MuiSelect-select.MuiSelect-outlined.MuiOutlinedInput-input.MuiInputBase-input.css-11u53oe-MuiSelect-select-MuiInputBase-input-MuiOutlinedInput-input': {
                padding: '5px',
              },
            }}
          >
            <MenuItem value={'all'}>All Properties</MenuItem>
            <MenuItem value={'map'}>Map Selection</MenuItem>
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={2} sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <Button
          className="mapFilterButton"
          size="small"
          onClick={() => toggleDrawer()}
          sx={{
            fontSize: '16',
            background: COLOR_PRIMARY_2,
            color: '#ffffff',
            '&:hover': { backgroundColor: COLOR_PRIMARY_1 },
          }}
        >
          <FilterAltOutlinedIcon sx={{ fontSize: 18, color: 'inherit' }} />
          All Filters
        </Button>
      </Grid>
    </Grid>
  );
};

const MarketExplorerTab = ({ toggleDrawer, currency, openDemoBookingForm, selectedCountry }) => {
  const dispatch = useDispatch();
  const [marketType, setMarketType] = useState('market');
  const [timePeriod, setTimePeriod] = useState('ltm');
  const [mapSelection, setMapSelection] = useState('all');
  const [listingRange, setListingRange] = useState<number[]>([10, 100000]);
  const marketExplorerTableColumn: GridColDef[] = [
    { field: 'id', headerName: 'ID', width: 200, hide: true },
    {
      field: 'marketName',
      headerName: 'Market Name',
      width: 150,
      editable: false,
    },
    {
      field: 'marketState',
      headerName: 'Region',
      width: 150,
      editable: false,
    },
    {
      field: 'annualSupply',
      headerName: 'Annual Supply',
      width: 150,
      editable: false,
      renderCell: (params: any) => {
        const isDataHidden: boolean = params.row.annualSupply ? false : true;
        return (
          <Typography
            style={{
              filter: `blur(${isDataHidden ? '4px' : 0})`,
              pointerEvents: isDataHidden ? 'none' : 'auto',
              userSelect: isDataHidden ? 'none' : 'auto',
            }}
          >
            {isDataHidden
              ? 3000
              : params.value != 'N/A'
              ? formatCurrency(String(params.value))
              : 'N/A'}
          </Typography>
        );
      },
    },
    {
      field: 'supplyPercChange',
      headerName: '1y Supply % Change',
      width: 150,
      editable: false,
      renderCell: (params: any) => {
        const isDataHidden: boolean = params.row.supplyPercChange ? false : true;
        return (
          <Typography
            style={{
              filter: `blur(${isDataHidden ? '4px' : 0})`,
              pointerEvents: isDataHidden ? 'none' : 'auto',
              userSelect: isDataHidden ? 'none' : 'auto',
            }}
          >
            {isDataHidden ? 30 : params.value != 'N/A' ? `${params.value.toFixed(2)}%` : 'N/A'}
          </Typography>
        );
      },
    },
    {
      field: 'supplyPercChangeTwoYears',
      headerName: '2Y Supply % Change',
      width: 150,
      editable: false,
      renderCell: (params: any) => {
        const isDataHidden: boolean = params.row.supplyPercChange ? false : true;
        return (
          <Typography
            style={{
              filter: `blur(${isDataHidden ? '4px' : 0})`,
              pointerEvents: isDataHidden ? 'none' : 'auto',
              userSelect: isDataHidden ? 'none' : 'auto',
            }}
          >
            {isDataHidden ? 30 : params.value != 'N/A' ? `${params.value.toFixed(2)}%` : 'N/A'}
          </Typography>
        );
      },
    },
    {
      field: 'percStillAvailiableFromLastYear',
      headerName: '% still available from last year',
      width: 150,
      editable: false,
      renderCell: (params: any) => {
        const isDataHidden: boolean = params.row.percStillAvailiableFromLastYear ? false : true;

        if (params.row.id === 12 && isDataHidden) {
          return (
            <Button
              className="mapFilterButton"
              size="large"
              onClick={() => openDemoBookingForm()}
              sx={{
                fontSize: '16',
                background: COLOR_PRIMARY_2,
                color: '#ffffff',
                '&:hover': { backgroundColor: COLOR_PRIMARY_1 },
              }}
            >
              Schedule a Demo
            </Button>
          );
        } else {
          return (
            <Typography
              style={{
                filter: `blur(${isDataHidden ? '4px' : 0})`,
                pointerEvents: isDataHidden ? 'none' : 'auto',
                userSelect: isDataHidden ? 'none' : 'auto',
              }}
            >
              {isDataHidden ? 60 : params.value !== 'N/A' ? `${params.value.toFixed(2)}%` : 'N/A'}
            </Typography>
          );
        }
      },
    },
    {
      field: 'hostStat1To5',
      headerName: '1-5 listings hosts %',
      width: 150,
      editable: false,
      renderCell: (params: any) => {
        const isDataHidden: boolean = params.row.hostStat1To5 ? false : true;
        return (
          <Typography
            style={{
              filter: `blur(${isDataHidden ? '4px' : 0})`,
              pointerEvents: isDataHidden ? 'none' : 'auto',
              userSelect: isDataHidden ? 'none' : 'auto',
            }}
          >
            {isDataHidden ? 30 : params.value != 'N/A' ? `${params.value.toFixed(2)}%` : 'N/A'}
          </Typography>
        );
      },
    },
    {
      field: 'hostStat6To20',
      headerName: '6-20 listings hosts %',
      width: 150,
      editable: false,
      renderCell: (params: any) => {
        const isDataHidden: boolean = params.row.hostStat6To20 ? false : true;
        return (
          <Typography
            style={{
              filter: `blur(${isDataHidden ? '4px' : 0})`,
              pointerEvents: isDataHidden ? 'none' : 'auto',
              userSelect: isDataHidden ? 'none' : 'auto',
            }}
          >
            {isDataHidden ? 30 : params.value != 'N/A' ? `${params.value.toFixed(2)}%` : 'N/A'}
          </Typography>
        );
      },
    },
    {
      field: 'hostStat20Plus',
      headerName: '20+ listings hosts %',
      width: 150,
      editable: false,
      renderCell: (params: any) => {
        const isDataHidden: boolean = params.row.hostStat20Plus ? false : true;
        return (
          <Typography
            style={{
              filter: `blur(${isDataHidden ? '4px' : 0})`,
              pointerEvents: isDataHidden ? 'none' : 'auto',
              userSelect: isDataHidden ? 'none' : 'auto',
            }}
          >
            {isDataHidden ? 30 : params.value != 'N/A' ? `${params.value.toFixed(2)}%` : 'N/A'}
          </Typography>
        );
      },
    },
    {
      field: 'AvgOccupancyRate',
      headerName: 'Average Occupancy Rate',
      width: 150,
      editable: false,
      renderCell: (params: any) => {
        const isDataHidden: boolean = params.row.AvgOccupancyRate ? false : true;
        return (
          <Typography
            style={{
              filter: `blur(${isDataHidden ? '4px' : 0})`,
              pointerEvents: isDataHidden ? 'none' : 'auto',
              userSelect: isDataHidden ? 'none' : 'auto',
            }}
          >
            {isDataHidden ? 60 : params.value != 'N/A' ? `${params.value}%` : 'N/A'}
          </Typography>
        );
      },
    },
    {
      field: 'AvgOccupancyRatePercChange',
      headerName: '1Y Avg. Occupancy Rate % Change',
      width: 150,
      editable: false,
      renderCell: (params: any) => {
        const isDataHidden: boolean = params.row.AvgOccupancyRate ? false : true;
        return (
          <Typography
            style={{
              filter: `blur(${isDataHidden ? '4px' : 0})`,
              pointerEvents: isDataHidden ? 'none' : 'auto',
              userSelect: isDataHidden ? 'none' : 'auto',
            }}
          >
            {isDataHidden ? 30 : params.value != 'N/A' ? `${params.value.toFixed(2)}%` : 'N/A'}
          </Typography>
        );
      },
    },
    {
      field: 'AvgOccupancyRatePercChangeTwoYears',
      headerName: '2Y Avg. Occupancy Rate % Change',
      width: 150,
      editable: false,
      renderCell: (params: any) => {
        const isDataHidden: boolean = params.row.AvgOccupancyRate ? false : true;
        return (
          <Typography
            style={{
              filter: `blur(${isDataHidden ? '4px' : 0})`,
              pointerEvents: isDataHidden ? 'none' : 'auto',
              userSelect: isDataHidden ? 'none' : 'auto',
            }}
          >
            {isDataHidden ? 30 : params.value != 'N/A' ? `${params.value.toFixed(2)}%` : 'N/A'}
          </Typography>
        );
      },
    },
    {
      field: 'adr',
      headerName: 'ADR',
      width: 100,
      editable: false,
      renderCell: (params: any) => {
        const isDataHidden: boolean = params.row.adr ? false : true;
        return (
          <Typography
            style={{
              filter: `blur(${isDataHidden ? '4px' : 0})`,
              pointerEvents: isDataHidden ? 'none' : 'auto',
              userSelect: isDataHidden ? 'none' : 'auto',
            }}
          >
            {isDataHidden
              ? 300
              : params.value != 'N/A'
              ? formatCurrency(String(convertCurrency(currency, params.value)))
              : 'N/A'}
          </Typography>
        );
      },
    },
    {
      field: 'adrPercChange',
      headerName: '1Y ADR % Change',
      width: 150,
      editable: false,
      renderCell: (params: any) => {
        const isDataHidden: boolean = params.row.adr ? false : true;
        return (
          <Typography
            style={{
              filter: `blur(${isDataHidden ? '4px' : 0})`,
              pointerEvents: isDataHidden ? 'none' : 'auto',
              userSelect: isDataHidden ? 'none' : 'auto',
            }}
          >
            {isDataHidden ? 30 : params.value != 'N/A' ? `${params.value.toFixed(2)}%` : 'N/A'}
          </Typography>
        );
      },
    },
    {
      field: 'adrPercChangeTwoYears',
      headerName: '2Y ADR % Change',
      width: 150,
      editable: false,
      renderCell: (params: any) => {
        const isDataHidden: boolean = params.row.adr ? false : true;
        return (
          <Typography
            style={{
              filter: `blur(${isDataHidden ? '4px' : 0})`,
              pointerEvents: isDataHidden ? 'none' : 'auto',
              userSelect: isDataHidden ? 'none' : 'auto',
            }}
          >
            {isDataHidden ? 30 : params.value != 'N/A' ? `${params.value.toFixed(2)}%` : 'N/A'}
          </Typography>
        );
      },
    },
    {
      field: 'revenue',
      headerName: 'Revenue',
      width: 100,
      editable: false,
      renderCell: (params: any) => {
        const isDataHidden: boolean = params.row.revenue ? false : true;
        return (
          <Typography
            style={{
              filter: `blur(${isDataHidden ? '4px' : 0})`,
              pointerEvents: isDataHidden ? 'none' : 'auto',
              userSelect: isDataHidden ? 'none' : 'auto',
            }}
          >
            {isDataHidden
              ? 200
              : params.value != 'N/A'
              ? formatCurrency(String(convertCurrency(currency, params.value)))
              : 'N/A'}
          </Typography>
        );
      },
    },
    {
      field: 'revenuePercChange',
      headerName: '1Y Revenue % Change',
      width: 150,
      editable: false,
      renderCell: (params: any) => {
        const isDataHidden: boolean = params.row.revenue ? false : true;
        return (
          <Typography
            style={{
              filter: `blur(${isDataHidden ? '4px' : 0})`,
              pointerEvents: isDataHidden ? 'none' : 'auto',
              userSelect: isDataHidden ? 'none' : 'auto',
            }}
          >
            {isDataHidden ? 30 : params.value != 'N/A' ? `${params.value.toFixed(2)}%` : 'N/A'}
          </Typography>
        );
      },
    },
    {
      field: 'revenuePercChangeTwoYears',
      headerName: '2Y Revenue % Change',
      width: 150,
      editable: false,
      renderCell: (params: any) => {
        const isDataHidden: boolean = params.row.revenue ? false : true;
        return (
          <Typography
            style={{
              filter: `blur(${isDataHidden ? '4px' : 0})`,
              pointerEvents: isDataHidden ? 'none' : 'auto',
              userSelect: isDataHidden ? 'none' : 'auto',
            }}
          >
            {isDataHidden ? 30 : params.value != 'N/A' ? `${params.value.toFixed(2)}%` : 'N/A'}
          </Typography>
        );
      },
    },
    {
      field: 'avgLOS',
      headerName: 'Average Length of Stay',
      width: 150,
      editable: false,
      renderCell: (params: any) => {
        const isDataHidden: boolean = params.row.avgLOS ? false : true;
        return (
          <Typography
            style={{
              filter: `blur(${isDataHidden ? '4px' : 0})`,
              pointerEvents: isDataHidden ? 'none' : 'auto',
              userSelect: isDataHidden ? 'none' : 'auto',
            }}
          >
            {isDataHidden ? 10 : params.value != 'N/A' ? `${params.value.toFixed(2)}%` : 'N/A'}
          </Typography>
        );
      },
    },
    {
      field: 'avgLOSPercChange',
      headerName: 'Average Length of Stay % change',
      width: 150,
      editable: false,
      renderCell: (params: any) => {
        const isDataHidden: boolean = params.row.avgLOSPercChange ? false : true;
        return (
          <Typography
            style={{
              filter: `blur(${isDataHidden ? '4px' : 0})`,
              pointerEvents: isDataHidden ? 'none' : 'auto',
              userSelect: isDataHidden ? 'none' : 'auto',
            }}
          >
            {isDataHidden ? 3 : params.value != 'N/A' ? `${params.value.toFixed(2)}%` : 'N/A'}
          </Typography>
        );
      },
    },
    {
      field: 'avgBookingLeadTime',
      headerName: 'Average Booking Lead Time',
      width: 150,
      editable: false,
      renderCell: (params: any) => {
        const isDataHidden: boolean = params.row.avgBookingLeadTime ? false : true;
        return (
          <Typography
            style={{
              filter: `blur(${isDataHidden ? '4px' : 0})`,
              pointerEvents: isDataHidden ? 'none' : 'auto',
              userSelect: isDataHidden ? 'none' : 'auto',
            }}
          >
            {isDataHidden ? 15 : params.value != 'N/A' ? `${params.value.toFixed(2)}%` : 'N/A'}
          </Typography>
        );
      },
    },
    {
      field: 'avgBookingLeadTimePercChange',
      headerName: 'Average booking lead time % change',
      width: 150,
      editable: false,
      renderCell: (params: any) => {
        const isDataHidden: boolean = params.row.avgBookingLeadTimePercChange ? false : true;
        return (
          <Typography
            style={{
              filter: `blur(${isDataHidden ? '4px' : 0})`,
              pointerEvents: isDataHidden ? 'none' : 'auto',
              userSelect: isDataHidden ? 'none' : 'auto',
            }}
          >
            {isDataHidden ? 3 : params.value != 'N/A' ? `${params.value.toFixed(2)}%` : 'N/A'}
          </Typography>
        );
      },
    },
  ];

  const [marketExplorerRows, setMarketExplorerRows] = useState([]);

  const explorerMarkets = useSelector(state => (state as any).dashboard.explorerMarkets);

  useEffect(() => {
    dispatch(getExplorerMarkets(selectedCountry, marketType));
  }, [marketType, selectedCountry]);

  useEffect(() => {
    if (!explorerMarkets.loading && explorerMarkets.data) {
      setMarketExplorerRows(getMarketExplorerData(explorerMarkets.data, listingRange, timePeriod));
    }
  }, [explorerMarkets, listingRange, currency, timePeriod]);

  return (
    <Grid container item xs={12} sx={{ height: '80vh' }}>
      <Grid item xs={12} sx={{ height: '90px' }}>
        <CustomFilters
          toggleDrawer={toggleDrawer}
          marketType={marketType}
          setMarketType={setMarketType}
          timePeriod={timePeriod}
          setTimePeriod={setTimePeriod}
          mapSelection={mapSelection}
          setMapSelection={setMapSelection}
          listingRange={listingRange}
          setListingRange={setListingRange}
          filterUsedIn="marketExplorer"
          openDemoBookingForm={openDemoBookingForm}
        />
      </Grid>
      <Grid container item xs={12} sx={{ height: 'calc(100% - 90px)' }}>
        <Grid item xs={12} sx={{ height: 'calc(100% - 40px)' }}>
          <Box sx={{ height: '100%', width: '100%' }}>
            {explorerMarkets.loading ? (
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <CircularProgress />
              </div>
            ) : (
              <DataGrid
                sortingOrder={['desc', 'asc']}
                sx={{ backgroundColor: 'white' }}
                rows={marketExplorerRows}
                columns={marketExplorerTableColumn}
                disableColumnMenu={true}
                components={{ Toolbar: (props) => <CustomToolbar {...props} fileName={`${selectedCountry}_Market_Explorer_Airbtics.csv`} />  }}
              />
            )}
          </Box>
        </Grid>
      </Grid>
    </Grid>
  );
};

const MarketSummaryTab = ({
  toggleDrawer,
  currency,
  openDemoBookingForm,
  globalFilteredListingIds,
  filterActive,
  selectedMarket,
}) => {
  const advancedListings = useSelector(state => (state as any).dashboard.advancedListings);
  const [granualityType, setGranualityType] = useState('monthly');
  const [timePeriod, setTimePeriod] = useState('ltm');
  const [mapSelection, setMapSelection] = useState('all');
  const [listingRange, setListingRange] = useState<number[]>([100, 2000]);
  const [startMonth, setStartMonth] = useState<number>(1);
  const [startYear, setStartYear] = useState<number>(2021);
  const [endMonth, setEndMonth] = useState<number>(4);
  const [endYear, setEndYear] = useState<number>(2024);
  const [marketSummaryRows, setMarketSummaryRows] = useState([]);

  const marketSummaryTableColumn: GridColDef[] = [
    { field: 'id', headerName: 'ID', width: 200, hide: true },
    {
      field: 'date',
      headerName: 'Date',
      width: 150,
      editable: false,
    },
    {
      field: 'availiableListings',
      headerName: 'Availiable Listings',
      width: 150,
      editable: false,
      renderCell: (params: any) => (
        <Typography>{params.value ? formatCurrency(String(params.value)) : 'N/A'}</Typography>
      ),
    },
    {
      field: 'bookedListings',
      headerName: 'Booked Listings',
      width: 150,
      editable: false,
      renderCell: (params: any) => (
        <Typography>{params.value ? formatCurrency(String(params.value)) : 'N/A'}</Typography>
      ),
    },
    {
      field: 'availiableListingNights',
      headerName: 'Availiable Listing Nights',
      width: 150,
      editable: false,
      renderCell: (params: any) => (
        <Typography>{params.value ? formatCurrency(String(params.value)) : 'N/A'}</Typography>
      ),
    },
    {
      field: 'bookedNights',
      headerName: 'Booked Nights',
      width: 150,
      editable: false,
      renderCell: (params: any) => (
        <Typography>{params.value ? formatCurrency(String(params.value)) : 'N/A'}</Typography>
      ),
    },
    {
      field: 'revenue',
      headerName: 'Revenue',
      width: 150,
      editable: false,
      renderCell: (params: any) => (
        <Typography>
          {exchangeRates[currency].symbol}
          {params.value != -1
            ? formatCurrency(String(convertCurrency(currency, params.value)))
            : 'N/A'}
        </Typography>
      ),
    },
    {
      field: 'avgOccupancy',
      headerName: 'Average Occupancy',
      width: 150,
      editable: false,
      renderCell: (params: any) => <Typography>{params.value + '%'}</Typography>,
    },
    {
      field: 'adr',
      headerName: 'ADR',
      width: 150,
      editable: false,
      renderCell: (params: any) => (
        <Typography>
          {exchangeRates[currency].symbol}
          {params.value != -1
            ? formatCurrency(String(convertCurrency(currency, params.value)))
            : 'N/A'}
        </Typography>
      ),
    },
    {
      field: 'avgLOS',
      headerName: 'Average Length of Stay',
      width: 150,
      editable: false,
    },
    {
      field: 'avgBookingLeadTime',
      headerName: 'Average Booking Lead Time',
      width: 150,
      editable: false,
    },
  ];

  useEffect(() => {
    if (endYear == 2024) {
      const validMonths = [1, 2, 3, 4];
      if (!validMonths.includes(endMonth)) {
        setEndMonth(4);
      }
    }
  }, [endMonth, endYear]);

  useEffect(() => {
    if (!advancedListings.loading && advancedListings.data) {
      const marketSummaryData = aggregateToMarketLevel(
        advancedListings.data,
        globalFilteredListingIds,
        filterActive
      );
      setMarketSummaryRows(
        getMarketSummaryData(
          marketSummaryData,
          startMonth,
          startYear,
          endMonth,
          endYear,
          granualityType,
          currency
        )
      );
    }
  }, [
    advancedListings,
    startMonth,
    startYear,
    endMonth,
    endYear,
    granualityType,
    currency,
    globalFilteredListingIds,
    filterActive,
  ]);

  return (
    <Grid container item xs={12} sx={{ height: '80vh' }}>
      <Grid item xs={12} sx={{ height: '100px' }}>
        <CustomMarketSummaryFilters
          toggleDrawer={toggleDrawer}
          granualityType={granualityType}
          setGranualityType={setGranualityType}
          mapSelection={mapSelection}
          setMapSelection={setMapSelection}
          startMonth={startMonth}
          setStartMonth={setStartMonth}
          startYear={startYear}
          setStartYear={setStartYear}
          endMonth={endMonth}
          setEndMonth={setEndMonth}
          endYear={endYear}
          setEndYear={setEndYear}
          openDemoBookingForm={openDemoBookingForm}
        />
      </Grid>
      <Grid container item xs={12} sx={{ height: 'calc(100% - 100px)' }}>
        <Grid item xs={12} sx={{ height: '100%' }}>
          <Box sx={{ height: '100%', width: '100%' }}>
            {advancedListings.loading ? (
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <CircularProgress />
              </div>
            ) : (
              <DataGrid
                sortingOrder={['desc', 'asc']}
                sx={{ backgroundColor: 'white' }}
                rows={marketSummaryRows}
                columns={marketSummaryTableColumn}
                disableColumnFilter={true}
                disableColumnMenu={true}
                components={{ Toolbar: (props) => <CustomToolbar {...props} fileName={`${selectedMarket.district}_Market_Summary_Airbtics.csv`} />  }}
              />
            )}
          </Box>
        </Grid>
      </Grid>
    </Grid>
  );
};

const PropertyManagerTab = ({
  toggleDrawer,
  currency,
  openDemoBookingForm,
  globalFilteredListingIds,
  filterActive,
  selectedMarket,
}) => {
  const [marketType, setMarketType] = useState('market');
  const [timePeriod, setTimePeriod] = useState('ltm');
  const [mapSelection, setMapSelection] = useState('all');
  const [listingRange, setListingRange] = useState<number[]>([1, 155]);
  const propertyManagerTableColumn: GridColDef[] = [
    { field: 'id', headerName: 'ID', width: 200, hide: true, sortable: true },
    {
      field: 'hostID',
      headerName: 'Host ID',
      width: 150,
      editable: false,
      sortable: true,
      renderCell: (params: any) => {
        const hostID = params.value;
        const url = `https://www.airbnb.com/users/show/${hostID}`;
        return (
          <a href={url} target="_blank" rel="noopener noreferrer">
            {hostID}
          </a>
        );
      },
    },
    {
      field: 'hostName',
      headerName: 'Host Name',
      width: 200,
      editable: false,
      sortable: true,
    },
    {
      field: 'noOfListings',
      headerName: 'Number Of Listings',
      width: 150,
      editable: false,
      sortable: true,
    },
    {
      field: 'adr',
      headerName: 'Average ADR',
      width: 150,
      editable: false,
      sortable: true,
      renderCell: (params: any) => (
        <Typography>
          {exchangeRates[currency].symbol}
          {params.value != -1
            ? formatCurrency(String(convertCurrency(currency, params.value)))
            : 'N/A'}
        </Typography>
      ),
    },
    {
      field: 'revenue',
      headerName: 'Revenue',
      width: 150,
      editable: false,
      sortable: true,
      renderCell: (params: any) => (
        <Typography>
          {exchangeRates[currency].symbol}
          {params.value != -1
            ? formatCurrency(String(convertCurrency(currency, params.value)))
            : 'N/A'}
        </Typography>
      ),
    },
    {
      field: 'propertyReviews',
      headerName: 'Property Reviews Count',
      width: 150,
      editable: false,
      sortable: true,
    },
    {
      field: 'avgPropertyRating',
      headerName: 'Average Property Rating',
      width: 150,
      editable: false,
      sortable: true,
    },
  ];
  const advancedListings = useSelector(state => (state as any).dashboard.advancedListings);
  let propertyManagerRows = aggregateMarketLevelPropertyManagerData(
    advancedListings.data,
    listingRange,
    timePeriod,
    globalFilteredListingIds,
    filterActive
  );

  return (
    <Grid container item xs={12} sx={{ height: '80vh' }}>
      <Grid item xs={12} sx={{ height: '100px' }}>
        <CustomFilters
          toggleDrawer={toggleDrawer}
          marketType={marketType}
          setMarketType={setMarketType}
          timePeriod={timePeriod}
          setTimePeriod={setTimePeriod}
          mapSelection={mapSelection}
          setMapSelection={setMapSelection}
          listingRange={listingRange}
          setListingRange={setListingRange}
          filterUsedIn="propertyManager"
          openDemoBookingForm={openDemoBookingForm}
        />
      </Grid>
      <Grid container item xs={12} sx={{ height: 'calc(100% - 100px)' }}>
        <Grid item xs={12} sx={{ height: '100%' }}>
          <Box sx={{ height: '100%', width: '100%' }}>
            {advancedListings.loading ? (
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <CircularProgress />
              </div>
            ) : (
              <DataGrid
                sortingOrder={['desc', 'asc']}
                sx={{ backgroundColor: 'white' }}
                rows={propertyManagerRows}
                columns={propertyManagerTableColumn}
                disableColumnFilter={true}
                disableColumnMenu={true}
                components={{ Toolbar: (props) => <CustomToolbar {...props} fileName={`${selectedMarket.district}_Property_Manager_Airbtics.csv`} />  }}
              />
            )}
          </Box>
        </Grid>
      </Grid>
    </Grid>
  );
};

const CustomRenderCellForComps = ({ params }) => {
  let lastSeen = new Date(params?.row?.lastSeen);
  const dateBefore14Days = dateDeforeNDays(14);

  if (lastSeen >= dateBefore14Days) {
    return (
      <Box sx={{ height: '100%', width: '100%', display: 'flex', alignItems: 'center' }}>
        <Typography variant="body2">{params['value']}</Typography>
      </Box>
    );
  } else {
    return (
      <Tooltip
        title={`This listing was last observed by our system on ${params?.row?.lastSeen}, so this may not be on Airbnb anymore`}
        arrow
      >
        <Box sx={{ height: '100%', width: '100%', display: 'flex', alignItems: 'center' }}>
          <Typography variant="body2">{params.value}</Typography>
        </Box>
      </Tooltip>
    );
  }
};

const getCompsTableColumns = (granualityType, timePeriod, currency, dateList, months) => {
  let compsTableColumns: GridColDef[] = [
    { field: 'id', headerName: 'ID', width: 200, hide: true },
    {
      field: 'listingID',
      headerName: 'Airbnb Listing ID',
      width: 200,
      editable: false,
      renderCell: (params: any) => <CustomRenderCellForComps params={params} />,
    },
    {
      field: 'title',
      headerName: 'Listing Title',
      width: 150,
      editable: false,
      renderCell: (params: any) => <CustomRenderCellForComps params={params} />,
    },
    {
      field: 'propertyType',
      headerName: 'Property Type',
      width: 150,
      editable: false,
      renderCell: (params: any) => <CustomRenderCellForComps params={params} />,
    },
    {
      field: 'listingType',
      headerName: 'Listing Type',
      width: 150,
      editable: false,
      renderCell: (params: any) => <CustomRenderCellForComps params={params} />,
    },
    {
      field: 'lastScrappedDate',
      headerName: 'Last Scrapped Date',
      width: 150,
      editable: false,
      renderCell: (params: any) => <CustomRenderCellForComps params={params} />,
    },
    {
      field: 'country',
      headerName: 'Country',
      width: 150,
      editable: false,
      renderCell: (params: any) => <CustomRenderCellForComps params={params} />,
    },
    {
      field: 'city',
      headerName: 'City',
      width: 150,
      editable: false,
      renderCell: (params: any) => <CustomRenderCellForComps params={params} />,
    },
    {
      field: 'noOfReviews',
      headerName: 'Number Of Reviews',
      width: 150,
      editable: false,
      renderCell: (params: any) => <CustomRenderCellForComps params={params} />,
    },
    {
      field: 'bedrooms',
      headerName: 'Bedrooms',
      width: 150,
      editable: false,
      renderCell: (params: any) => <CustomRenderCellForComps params={params} />,
    },
    {
      field: 'bathrooms',
      headerName: 'Bathrooms',
      width: 150,
      editable: false,
      renderCell: (params: any) => <CustomRenderCellForComps params={params} />,
    },
    {
      field: 'maxGuests',
      headerName: 'Max Guests',
      width: 150,
      editable: false,
      renderCell: (params: any) => <CustomRenderCellForComps params={params} />,
    },
    {
      field: 'airbnbSuperhost',
      headerName: 'Airbnb Superhost',
      width: 150,
      editable: false,
      renderCell: (params: any) => <CustomRenderCellForComps params={params} />,
    },
    {
      field: 'cancellationPolicy',
      headerName: 'Cancellation Policy',
      width: 150,
      editable: false,
      renderCell: (params: any) => <CustomRenderCellForComps params={params} />,
    },
    {
      field: 'cleaningFee',
      headerName: 'Cleaning Fee (USD)',
      width: 150,
      editable: false,
      renderCell: (params: any) => (
        <Typography>
          {exchangeRates[currency].symbol}
          {params.value != -1
            ? formatCurrency(String(convertCurrency(currency, params.value)))
            : 'N/A'}
        </Typography>
      ),
    },
    {
      field: 'extraPeopleFee',
      headerName: 'Extra People Fee',
      width: 150,
      editable: false,
      renderCell: (params: any) => (
        <Typography>
          {exchangeRates[currency].symbol}
          {params.value != -1
            ? formatCurrency(String(convertCurrency(currency, params.value)))
            : 'N/A'}
        </Typography>
      ),
    },
    {
      field: 'checkin',
      headerName: 'Check-in Time',
      width: 150,
      editable: false,
      renderCell: (params: any) => <CustomRenderCellForComps params={params} />,
    },
    {
      field: 'checkout',
      headerName: 'Check-out Time',
      width: 150,
      editable: false,
      renderCell: (params: any) => <CustomRenderCellForComps params={params} />,
    },
    {
      field: 'minimumStay',
      headerName: 'Minimum Stay',
      width: 150,
      editable: false,
      renderCell: (params: any) => <CustomRenderCellForComps params={params} />,
    },
    {
      field: 'latitude',
      headerName: 'Latitude',
      width: 150,
      editable: false,
      renderCell: (params: any) => <CustomRenderCellForComps params={params} />,
    },
    {
      field: 'longitude',
      headerName: 'Longitude',
      width: 150,
      editable: false,
      renderCell: (params: any) => <CustomRenderCellForComps params={params} />,
    },
    {
      field: 'exactLocation',
      headerName: 'Exact Location',
      width: 150,
      editable: false,
      renderCell: (params: any) => <CustomRenderCellForComps params={params} />,
    },
    {
      field: 'overallRating',
      headerName: 'Overall Rating',
      width: 150,
      editable: false,
      renderCell: (params: any) => <CustomRenderCellForComps params={params} />,
    },
    {
      field: 'accuracyRating',
      headerName: 'Airbnb Accuracy Rating',
      width: 150,
      editable: false,
      renderCell: (params: any) => <CustomRenderCellForComps params={params} />,
    },
    {
      field: 'cleanlinessRating',
      headerName: 'Airbnb Cleanliness Rating',
      width: 150,
      editable: false,
      renderCell: (params: any) => <CustomRenderCellForComps params={params} />,
    },
    {
      field: 'checkinRating',
      headerName: 'Airbnb Checkin Rating',
      width: 150,
      editable: false,
      renderCell: (params: any) => <CustomRenderCellForComps params={params} />,
    },
    {
      field: 'locationRating',
      headerName: 'Airbnb Location Rating',
      width: 150,
      editable: false,
      renderCell: (params: any) => <CustomRenderCellForComps params={params} />,
    },
    {
      field: 'valueRating',
      headerName: 'Airbnb Value Rating',
      width: 150,
      editable: false,
      renderCell: (params: any) => <CustomRenderCellForComps params={params} />,
    },
    {
      field: 'amenities',
      headerName: 'Amenities',
      width: 150,
      editable: false,
      renderCell: (params: any) => <CustomRenderCellForComps params={params} />,
    },
    {
      field: 'pictureURL',
      headerName: 'Picture URL',
      width: 150,
      editable: false,
      renderCell: (params: any) => <CustomRenderCellForComps params={params} />,
    },
    {
      field: 'hostID',
      headerName: 'Airbnb Host ID',
      width: 150,
      editable: false,
      renderCell: (params: any) => <CustomRenderCellForComps params={params} />,
    },
    {
      field: 'guestControls',
      headerName: 'Guest Controls',
      width: 150,
      editable: false,
      renderCell: (params: any) => <CustomRenderCellForComps params={params} />,
    },
    {
      field: 'instantBookable',
      headerName: 'Instant Bookable',
      width: 150,
      editable: false,
      renderCell: (params: any) => <CustomRenderCellForComps params={params} />,
    },
    {
      field: 'petsAllowed',
      headerName: 'Pets Allowed',
      width: 150,
      editable: false,
      renderCell: (params: any) => <CustomRenderCellForComps params={params} />,
    },
    {
      field: 'listingURL',
      headerName: 'Listing URL',
      width: 150,
      editable: false,
      renderCell: (params: any) => <CustomRenderCellForComps params={params} />,
    },
  ];

  let years = ['ltm', '2023', '2022', '2021'];

  if (granualityType == 'daily') {
    dateList.forEach(date => {
      compsTableColumns.push({
        field: `${date}`,
        headerName: `${date}`,
        width: 150,
        editable: false,
        renderCell: (params: any) => {
          if (params.value) {
            const [value, status] = params.value.split('+');
            return (
              <Box
                sx={{
                  backgroundColor: BOOKING_STATUS_COLOR[status],
                  height: '100%',
                  width: '100%',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <Typography sx={{ color: SIDEBAR_COLOR, fontWeight: 400 }}>
                  {exchangeRates[currency].symbol}
                  {formatCurrency(String(convertCurrency(currency, value)))}
                </Typography>
              </Box>
            );
          } else {
            return (
              <Box
                sx={{
                  height: '100%',
                  width: '100%',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <Typography sx={{ color: SIDEBAR_COLOR, fontWeight: 400 }}>N/A</Typography>
              </Box>
            );
          }
        },
        sortComparator: (v1, v2) => {
          let value1 = v1 ? parseFloat(v1.split('+')[0]) : 0;
          value1 = isNaN(value1) ? 0 : value1;
          let value2 = v2 ? parseFloat(v2.split('+')[0]) : 0;
          value2 = isNaN(value2) ? 0 : value2;
          return value1 - value2;
        },
      });
    });
  }

  if (granualityType == 'monthly') {
    months.forEach(month => {
      compsTableColumns.push({
        field: `availiableDays_${month}`,
        headerName: `Count Availiable Days ${month}`,
        width: 150,
        editable: false,
      });
      compsTableColumns.push({
        field: `blockedDays_${month}`,
        headerName: `Count Blocked Days ${month}`,
        width: 150,
        editable: false,
      });
      compsTableColumns.push({
        field: `occupancyRate_${month}`,
        headerName: `Occupancy Rate ${month}`,
        width: 150,
        editable: false,
        renderCell: (params: any) => (
          <Typography>
            {params.value ? params.value : 'inactive'}
            {params.value && '%'}
          </Typography>
        ),
      });
      compsTableColumns.push({
        field: `adr_${month}`,
        headerName: `Average Daily Rate ${month}`,
        width: 150,
        editable: false,
        renderCell: (params: any) => (
          <Typography>
            {params.value && exchangeRates[currency].symbol}
            {params.value
              ? formatCurrency(String(convertCurrency(currency, params.value)))
              : 'inactive'}
          </Typography>
        ),
      });
      compsTableColumns.push({
        field: `revenue_${month}`,
        headerName: `Revenue ${month}`,
        width: 150,
        editable: false,
        renderCell: (params: any) => (
          <Typography>
            {params.value && exchangeRates[currency].symbol}
            {params.value
              ? formatCurrency(String(convertCurrency(currency, params.value)))
              : 'inactive'}
          </Typography>
        ),
      });
    });
  }

  if (granualityType === 'yearly') {
    years.forEach(year => {
      if (timePeriod === year) {
        compsTableColumns.push({
          field: `availiableDays_${year}`,
          headerName: `Count Availiable Days ${year}`,
          width: 150,
          editable: false,
        });
        compsTableColumns.push({
          field: `blockedDays_${year}`,
          headerName: `Count Blocked Days ${year}`,
          width: 150,
          editable: false,
        });
        compsTableColumns.push({
          field: `occupancyRate_${year}`,
          headerName: `Occupancy Rate ${year}`,
          width: 150,
          editable: false,
          renderCell: (params: any) => (
            <Typography>
              {params.value ? params.value : 'inactive'}
              {params.value && '%'}
            </Typography>
          ),
        });
        compsTableColumns.push({
          field: `adr_${year}`,
          headerName: `Average Daily Rate ${year}`,
          width: 150,
          editable: false,
          renderCell: (params: any) => (
            <Typography>
              {params.value && exchangeRates[currency].symbol}
              {params.value
                ? formatCurrency(String(convertCurrency(currency, params.value)))
                : 'inactive'}
            </Typography>
          ),
        });
        compsTableColumns.push({
          field: `revenue_${year}`,
          headerName: `Revenue ${year}`,
          width: 150,
          editable: false,
          renderCell: (params: any) => (
            <Typography>
              {params.value && exchangeRates[currency].symbol}
              {params.value
                ? formatCurrency(String(convertCurrency(currency, params.value)))
                : 'inactive'}
            </Typography>
          ),
        });
        compsTableColumns.push({
          field: `revenuePotential_${year}`,
          headerName: `Revenue Potential ${year}`,
          width: 150,
          editable: false,
          renderCell: (params: any) => (
            <Typography>
              {params.value && exchangeRates[currency].symbol}
              {params.value
                ? formatCurrency(String(convertCurrency(currency, params.value)))
                : 'inactive'}
            </Typography>
          ),
        });
      }
    });
  }

  return compsTableColumns;
};

const CompsTab = ({
  toggleDrawer,
  currency,
  openDemoBookingForm,
  globalFilteredListingIds,
  filterActive,
  selectedMarket,
}) => {
  const [listingId, setListingID] = useState('');
  const [timePeriod, setTimePeriod] = useState('ltm');
  const [mapSelection, setMapSelection] = useState('all');
  const [granualityType, setGranualityType] = useState('yearly');
  const [openListingModal, setOpenListingModal] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);

  const startDate = '2024-05-30';
  const days = 365;
  const dateList: string[] = generateDateList(startDate, days);
  // const compsets: any = getCompetitorRates(compRatesObj, dateList, compMetadata);

  const [compsRowsData, setCompsRowsData] = useState([]);
  const [compsTabColumn, setCompsTabColumn] = useState<GridColDef<any, any, any>[]>([]);
  const advancedListings = useSelector(state => (state as any).dashboard.advancedListings);
  const listingsFutureData = useSelector(state => (state as any).dashboard.listingsFutureData);

  useEffect(() => {
    if (
      !advancedListings.loading &&
      advancedListings.data &&
      !listingsFutureData.loading &&
      listingsFutureData.data
    ) {
      const months = Object.keys(advancedListings.data[0]).filter(isYearMonthKey);
      setCompsTabColumn(
        getCompsTableColumns(granualityType, timePeriod, currency, dateList, months)
      );
      setCompsRowsData(
        getCompsData(
          advancedListings.data,
          listingsFutureData.data,
          globalFilteredListingIds,
          filterActive,
          listingId,
          timePeriod,
          currency,
          granualityType,
          dateList,
          months
        )
      );
    }
  }, [
    advancedListings,
    listingsFutureData,
    listingId,
    timePeriod,
    currency,
    granualityType,
    globalFilteredListingIds,
    filterActive,
  ]);

  const classes = useStyles();

  const handleRowClick = params => {
    setSelectedRow(params.row);
    setOpenListingModal(true);
  };

  const handleClose = () => {
    setOpenListingModal(false);
    setSelectedRow(null);
  };

  const getRowClassName = params => {
    let lastSeen = new Date(params?.row?.lastSeen);
    const dateBefore14Days = dateDeforeNDays(14);

    if (lastSeen >= dateBefore14Days) {
      return '';
    } else {
      return classes.greyRow;
    }
  };

  return (
    <Grid container item xs={12} sx={{ height: '80vh' }}>
      <Grid item xs={12} sx={{ height: '100px' }}>
        <CustomCompsFilters
          listingId={listingId}
          setListingID={setListingID}
          toggleDrawer={toggleDrawer}
          timePeriod={timePeriod}
          setTimePeriod={setTimePeriod}
          mapSelection={mapSelection}
          setMapSelection={setMapSelection}
          openDemoBookingForm={openDemoBookingForm}
          granualityType={granualityType}
          setGranualityType={setGranualityType}
          compsRowsData={advancedListings.data}
        />
      </Grid>
      <Grid container item xs={12} sx={{ height: 'calc(100% - 100px)' }}>
        <Grid item xs={12} sx={{ height: '100%' }}>
          <Box sx={{ height: '100%', width: '100%' }}>
            {advancedListings.loading ? (
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <CircularProgress />
              </div>
            ) : (
              <DataGrid
                sortingOrder={['desc', 'asc']}
                sx={{ backgroundColor: 'white', '.MuiDataGrid-row': { cursor: 'pointer' } }}
                rows={compsRowsData}
                columns={compsTabColumn}
                disableColumnMenu={true}
                onRowClick={handleRowClick}
                getRowClassName={getRowClassName}
                initialState={{
                  sorting: {
                    sortModel: [{ field: 'lastScrappedDate', sort: 'desc' }],
                  },
                }}
                components={{ Toolbar: (props) => <CustomToolbar {...props} fileName={`${selectedMarket.district}_Comps_Airbtics.csv`} />  }}
              />
            )}
            <ListingModal
              open={openListingModal}
              onClose={handleClose}
              rowData={selectedRow}
              advancedListings={advancedListings}
              listingsFutureData={listingsFutureData}
              currency={currency}
            />
          </Box>
        </Grid>
      </Grid>
    </Grid>
  );
};

const CustomTabPanel = ({
  value,
  index,
  toggleDrawer,
  currency,
  openDemoBookingForm,
  globalFilteredListingIds,
  filterActive,
  selectedCountry,
  selectedMarket,
}) => {
  return (
    <Box
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
    >
      {value === index && (
        <Box sx={{ p: 2 }}>
          {value == 0 && (
            <MarketExplorerTab
              toggleDrawer={toggleDrawer}
              currency={currency}
              openDemoBookingForm={openDemoBookingForm}
              selectedCountry={selectedCountry}
            />
          )}
          {value == 1 && (
            <MarketSummaryTab
              toggleDrawer={toggleDrawer}
              currency={currency}
              openDemoBookingForm={openDemoBookingForm}
              globalFilteredListingIds={globalFilteredListingIds}
              filterActive={filterActive}
              selectedMarket={selectedMarket}
            />
          )}
          {value == 2 && (
            <PropertyManagerTab
              toggleDrawer={toggleDrawer}
              currency={currency}
              openDemoBookingForm={openDemoBookingForm}
              globalFilteredListingIds={globalFilteredListingIds}
              filterActive={filterActive}
              selectedMarket={selectedMarket}
            />
          )}
          {value == 3 && (
            <CompsTab
              toggleDrawer={toggleDrawer}
              currency={currency}
              openDemoBookingForm={openDemoBookingForm}
              globalFilteredListingIds={globalFilteredListingIds}
              filterActive={filterActive}
              selectedMarket={selectedMarket}
            />
          )}
        </Box>
      )}
    </Box>
  );
};

const a11yProps = (index: number) => {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
  };
};

const AdvancedDashboard = ({
  matches,
  user,
  advancedTab,
  setAdvancedTab,
  dummyMarket,
}: {
  matches: boolean;
  user: any;
  advancedTab: number;
  setAdvancedTab: any;
  dummyMarket: any;
}) => {
  const dispatch = useDispatch();
  const [showDemoBookingForm, setShowDemoBookingForm] = useState<boolean>(false);
  const [selectedCountry, setSelectedCountry] = useState('GB');
  const [selectedMarket, setSelectedMarket] = useState({
    district: 'Havant',
    city: 'Hampshire',
    countryCode: 'GB',
  });
  const explorerMarkets = useSelector(state => (state as any).dashboard.explorerMarkets);
  const currency = useSelector(state => (state as any).dashboard.currency);
  const isDisabled = !user.isAdvancePlanUser;
  const [filterOpen, setFilterOpen] = useState(false);
  const [filterVal, setFilterVal] = useState({
    roomType: ['all'],
    bedrooms: [100],
    bathrooms: [100],
    revenueFilter: ['all'],
    priceTierFilter: ['all'],
    accommodates: [1, 16],
    minimum_nights: [1, 30],
    number_of_reviews: 'all',
    rating: [3.9, 5],
    hostType: 'both',
    amenities: {
      pool: null,
      parking: null,
      hot_tub: null,
      gym: null,
      pets: null,
      dryer: null,
      washer: null,
      kitchen: null,
      ac: null,
      heating: null,
      wifi: null,
      tv: null,
      bbq_grill: null,
      fireplace: null,
      breakfast: null,
      beachfront: null,
      amazing_views: null,
    },
    showAllBookingComListings: false,
  } as FilterVal);
  const priceTiers = useSelector(state => (state as any).dashboard.priceTiers);
  const [filterActive, setFilterActive] = useState(false);
  const [filterApplied, setFilterApplied] = useState(false);
  const [globalFilteredListingIds, setGlobalFilteredListingIds] = useState<string[]>([]);

  const advancedListings = useSelector(state => (state as any).dashboard.advancedListings);
  const listings = convertAdvancedMarkersToMarkers(advancedListings.data) as ListingsProp;
  const [revenueRange, setRevenueRange] = useState({});

  useEffect(() => {
    if (!advancedListings.loading) {
      let priceTierData = calculatePriceTiers(advancedListings.data, true);
      dispatch(
        updatePriceTiers({
          Budget: {
            range: [0, priceTierData[0]],
            toggled: false,
          },
          Economy: {
            range: [priceTierData[0], priceTierData[1]],
            toggled: false,
          },
          Midscale: {
            range: [priceTierData[1], priceTierData[2]],
            toggled: false,
          },
          Upscale: {
            range: [priceTierData[2], priceTierData[3]],
            toggled: false,
          },
          Luxury: {
            range: [priceTierData[3], 999999],
            toggled: false,
          },
        })
      );

      if (advancedListings.data) {
        let revenueLTMRange = calculateRevenuePotentialRange(advancedListings.data, true);
        let revenueLTMObj = {};
        for (let revenueLTM of revenueLTMRange) {
          revenueLTMObj[revenueLTM] = 'visible';
        }
        setRevenueRange(revenueLTMObj);
      }
    }
  }, [advancedListings.loading]);

  const toggleDrawer = () => {
    setFilterOpen(!filterOpen);
  };

  useEffect(() => {
    dispatch(getExplorerMarkets(selectedCountry));
    dispatch(
      getAdvancedMarkers(selectedMarket.district, selectedMarket.city, selectedMarket.countryCode)
    );
    dispatch(
      getFutureDailyData(selectedMarket.district, selectedMarket.city, selectedMarket.countryCode)
    );
  }, [selectedCountry, selectedMarket]);

  useEffect(() => {
    if (advancedTab == 0) {
      setTimeout(() => {
        setShowDemoBookingForm(true);
      }, 2000);
    }
  }, [advancedTab]);

  const closeBookingForm = () => {
    setShowDemoBookingForm(false);
  };

  const openDemoBookingForm = () => {
    setShowDemoBookingForm(true);
  };

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    if (isDisabled) {
      openDemoBookingForm();
    } else {
      setAdvancedTab(newValue);
    }
  };

  const applyFilter = (
    revenueRange: { [key: number]: 'visible' | 'hidden' } | undefined = undefined,
    onlyConsiderRevenueFilter: boolean = false,
    filtersToConsider: string[] = []
  ) => {
    setFilterVal((e: FilterVal) => {
      if (!listings.loading) {
        const [tempFilteredListings, _] = getFilteredListings(
          listings.data,
          [] as any,
          e,
          true,
          revenueRange,
          onlyConsiderRevenueFilter,
          filtersToConsider
        ) as [string[], string[]];
        setGlobalFilteredListingIds(tempFilteredListings);
      }
      return e;
    });
  };

  const resetFilter = () => {
    setFilterVal(prevFilterVal => ({
      ...prevFilterVal,
      roomType: ['all'],
      bedrooms: [100],
      bathrooms: [100],
      revenueFilter: ['all'],
      priceTierFilter: ['all'],
      accommodates: [1, 16],
      minimum_nights: [1, 30],
      number_of_reviews: 'all',
      rating: [3.9, 5],
      amenities: {
        pool: null,
        parking: null,
        hot_tub: null,
        gym: null,
        pets: null,
        dryer: null,
        washer: null,
        kitchen: null,
        ac: null,
        heating: null,
        wifi: null,
        tv: null,
        bbq_grill: null,
        fireplace: null,
        breakfast: null,
        beachfront: null,
        amazing_views: null,
      },
      hostType: 'both',
    }));
    setGlobalFilteredListingIds([]);
    setFilterActive(false);
  };

  return (
    <>
      <Grid
        container
        direction="row"
        className="dashboard__overview"
        xs={12}
        sx={{ backgroundColor: SIDEBAR_COLOR }}
      >
        <DemoBookingForm
          matches={matches}
          user={user}
          showDemoBookingForm={showDemoBookingForm}
          closeBookingForm={closeBookingForm}
        />
        <Box sx={{ width: '100%', padding: '10px' }}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider', position: 'relative' }}>
            <Tabs value={advancedTab} onChange={handleChange} aria-label="basic tabs example">
              <Tab label="Market Explorer" {...a11yProps(0)} />
              <Tab label="Market Summary" {...a11yProps(1)} />
              <Tab label="Property Manager" {...a11yProps(2)} />
              <Tab label="Comps" {...a11yProps(3)} />
            </Tabs>
            <Box sx={{ position: 'absolute', top: '5px', right: '10px' }}>
              {advancedTab != 0 && !explorerMarkets.loading && explorerMarkets.data.length > 0 && (
                <FormControl variant="standard" sx={{ margin: '5px 5px 0px 0px', width: '170px' }}>
                  <Select
                    labelId="demo-simple-select-standard-label"
                    id="demo-simple-select-standard"
                    value={JSON.stringify(selectedMarket)}
                    label=""
                    onChange={e => {
                      if (isDisabled) openDemoBookingForm();
                      else setSelectedMarket(JSON.parse(e.target.value as string));
                    }}
                    sx={{
                      '& .MuiSelect-select.MuiSelect-outlined.MuiOutlinedInput-input.MuiInputBase-input.css-11u53oe-MuiSelect-select-MuiInputBase-input-MuiOutlinedInput-input': {
                        padding: '5px',
                      },
                      '& .MuiSelect-select': {
                        color: COLOR_PRIMARY_1,
                        fontWeight: 500,
                      },
                    }}
                  >
                    {explorerMarkets.data.map(market => (
                      <MenuItem
                        key={market.district}
                        value={JSON.stringify({
                          district: market.district,
                          city: market.city,
                          countryCode: market.country_code,
                        })}
                      >
                        {market.district}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
              <FormControl variant="standard" sx={{ margin: '5px 5px 0px 0px', width: '150px' }}>
                <Select
                  labelId="demo-simple-select-standard-label"
                  id="demo-simple-select-standard"
                  value={selectedCountry}
                  label=""
                  onChange={e => {
                    if (isDisabled) openDemoBookingForm();
                    else setSelectedCountry(e.target.value);
                  }}
                  sx={{
                    '& .MuiSelect-select.MuiSelect-outlined.MuiOutlinedInput-input.MuiInputBase-input.css-11u53oe-MuiSelect-select-MuiInputBase-input-MuiOutlinedInput-input': {
                      padding: '5px',
                    },
                    '& .MuiSelect-select': {
                      color: COLOR_PRIMARY_1,
                      fontWeight: 500,
                    },
                  }}
                >
                  <MenuItem key={'saved'} value={'saved'}>
                    My Saved Markets
                  </MenuItem>
                  {countryListWithCode.map(country => (
                    <MenuItem key={country.code} value={country.code}>
                      {country.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Box>

          {isDisabled && (
            <Grid sx={{ marginTop: '10px' }}>
              <Alert severity="info">
                This is a sample dashboard. Only available on Scale plan.{' '}
                <Typography
                  component="a"
                  onClick={() => openDemoBookingForm()}
                  sx={{ textDecoration: 'underline', cursor: 'pointer' }}
                >
                  Schedule a Demo
                </Typography>{' '}
                to learn more.
              </Alert>
            </Grid>
          )}

          <CustomTabPanel
            value={advancedTab}
            index={0}
            toggleDrawer={toggleDrawer}
            currency={currency}
            openDemoBookingForm={openDemoBookingForm}
            globalFilteredListingIds={globalFilteredListingIds}
            filterActive={filterActive}
            selectedCountry={selectedCountry}
            selectedMarket={selectedMarket}
          />
          <CustomTabPanel
            value={advancedTab}
            index={1}
            toggleDrawer={toggleDrawer}
            currency={currency}
            openDemoBookingForm={openDemoBookingForm}
            globalFilteredListingIds={globalFilteredListingIds}
            filterActive={filterActive}
            selectedCountry={selectedCountry}
            selectedMarket={selectedMarket}
          />
          <CustomTabPanel
            value={advancedTab}
            index={2}
            toggleDrawer={toggleDrawer}
            currency={currency}
            openDemoBookingForm={openDemoBookingForm}
            globalFilteredListingIds={globalFilteredListingIds}
            filterActive={filterActive}
            selectedCountry={selectedCountry}
            selectedMarket={selectedMarket}
          />
          <CustomTabPanel
            value={advancedTab}
            index={3}
            toggleDrawer={toggleDrawer}
            currency={currency}
            openDemoBookingForm={openDemoBookingForm}
            globalFilteredListingIds={globalFilteredListingIds}
            filterActive={filterActive}
            selectedCountry={selectedCountry}
            selectedMarket={selectedMarket}
          />
        </Box>
      </Grid>

      {filterOpen && (
        <Filter
          listings={extractVisibleListings(listings)}
          allListings={listings}
          isDisabled={isDisabled}
          filterVal={filterVal}
          setFilterVal={setFilterVal}
          applyFilter={applyFilter}
          open={filterOpen}
          toggleDrawer={toggleDrawer}
          setFilterActive={setFilterActive}
          matches={matches}
          setFilterApplied={setFilterApplied}
          resetFilter={resetFilter}
          currency={currency}
          revPotentialRangeInvestment={revenueRange}
          setIndividualRevenuePotentialRange={setRevenueRange}
          priceTiers={priceTiers}
          handleOnSampleClick={() => openDemoBookingForm()}
          isListingLoading={
            advancedListings.loading || !advancedListings.data || advancedListings.data.length == 0
          }
          dummyMarket={dummyMarket}
        />
      )}

      <ToastContainer position="bottom-left" newestOnTop style={{ marginTop: '40px' }} />
    </>
  );
};

export default AdvancedDashboard;
